import React, { useState, useEffect } from 'react';
import { ChevronDown, Search, Users, Edit, Users as FamilyIcon, Clock, FileText, Plus, MessageCircle, Mail, Copy, Check, UserPlus } from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { upsertQualScore, upsertActionComment } from '../services/cloudSqlService';
import RunnerCoachNotes from './RunnerCoachNotes';
import RunnerFamilyMembers from './RunnerFamilyMembers';
import RunnerClubHistory from './RunnerClubHistory';
import RunnerOnboardingSurvey from './RunnerOnboardingSurvey';

const KnowYourRunner = ({
  cohortData = [],
  cohortLoading = false,
  cohortError = null,
  selectedDistance = 'All',
  setSelectedDistance,
  searchTerm = '',
  setSearchTerm,
  filterOptions = { distances: [] },
  currentSeason = null,
  availableSeasons = [],
  selectedSeason = null,
  setSelectedSeason,
  coachEmail = null,
  isAdmin = false,
  onAdminCoachChange = null,
  onAdminRoleToggle = null,
  adminRoleEnabled: adminRoleEnabledProp = false
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [seasonMenuOpen, setSeasonMenuOpen] = useState(false);
  const [distanceMenuOpen, setDistanceMenuOpen] = useState(false);
  const [transferDistanceMenuOpen, setTransferDistanceMenuOpen] = useState(false);
  const [coachesMenuOpen, setCoachesMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedRunner, setSelectedRunner] = useState(null);
  const [menuOpenFor, setMenuOpenFor] = useState(null); // Track which runner's menu is open
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [transferRunner, setTransferRunner] = useState(null);
  const [transferRunnerLevel, setTransferRunnerLevel] = useState(null);
  const [transferRunnerActivity, setTransferRunnerActivity] = useState(null);
  const [transferRunnerSegment, setTransferRunnerSegment] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedNewLevel, setSelectedNewLevel] = useState(null);
  const [selectedNewActivity, setSelectedNewActivity] = useState(null);
  const [selectedNewSegment, setSelectedNewSegment] = useState(null);
  const [transferLevelMenuOpen, setTransferLevelMenuOpen] = useState(false);
  const [transferComment, setTransferComment] = useState('');
  const [pendingTransfers, setPendingTransfers] = useState(new Set());
  
  // Defer runner state
  const [showDeferModal, setShowDeferModal] = useState(false);
  const [showDeferConfirmationModal, setShowDeferConfirmationModal] = useState(false);
  const [deferRunner, setDeferRunner] = useState(null);
  const [deferComment, setDeferComment] = useState('');
  const [pendingDefers, setPendingDefers] = useState(new Set());
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesRunner, setNotesRunner] = useState(null);
  const [notesContent, setNotesContent] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  
  // Season metrics state
  // eslint-disable-next-line no-unused-vars
  const [seasonMetrics, setSeasonMetrics] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [metricsLoading, setMetricsLoading] = useState(false);
  
  // Race timings state
  const [raceTimings, setRaceTimings] = useState(null);
  const [raceTimingsLoading, setRaceTimingsLoading] = useState(false);
  const [editingRaceTimings, setEditingRaceTimings] = useState(false);
  const [raceTimingsFormData, setRaceTimingsFormData] = useState({
    race_timings: '',
    race_pr: false,
    race_comments: '',
    race_distance_completed: ''
  });
  const [raceTimingsMessage, setRaceTimingsMessage] = useState({ type: '', text: '' });
  const [raceTimingsValidation, setRaceTimingsValidation] = useState({ race_timings: '' });
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(false);
  const [recentActivitiesOffset, setRecentActivitiesOffset] = useState(0);
  const [recentActivitiesHasMore, setRecentActivitiesHasMore] = useState(false);
  
  // Admin role toggle state - use prop if provided, otherwise use local state
  const [adminRoleEnabledLocal, setAdminRoleEnabledLocal] = useState(false);
  const adminRoleEnabled = adminRoleEnabledProp !== undefined ? adminRoleEnabledProp : adminRoleEnabledLocal;
  const [availableCoaches, setAvailableCoaches] = useState([]);
  const [selectedAdminCoach, setSelectedAdminCoach] = useState(null); // Stores coach name
  
  // Edit state for meso metrics
  const [editingMeso, setEditingMeso] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editValidationErrors, setEditValidationErrors] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [editMessage, setEditMessage] = useState({ type: '', text: '' });
  const [copiedItems, setCopiedItems] = useState({});

  // Fetch pending action requests from database
  const fetchPendingActionRequests = async () => {
    try {
      let query = supabase
        .from('rhwb_action_requests')
        .select('runner_email_id, action_type, status')
        .eq('status', 'pending')
        .eq('latest_row', true);

      // Add season filter if selectedSeason is available
      if (selectedSeason) {
        query = query.eq('season', selectedSeason);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pending action requests:', error);
        return;
      }

      // Separate transfers and defers
      const transferEmails = new Set();
      const deferEmails = new Set();

      data?.forEach(request => {
        if (request.action_type === 'Transfer Runner') {
          transferEmails.add(request.runner_email_id);
        } else if (request.action_type === 'Defer Runner') {
          deferEmails.add(request.runner_email_id);
        }
      });

      setPendingTransfers(transferEmails);
      setPendingDefers(deferEmails);
    } catch (error) {
      console.error('Error in fetchPendingActionRequests:', error);
    }
  };

  // Load pending action requests when component mounts
  useEffect(() => {
    fetchPendingActionRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load available coaches when admin toggle is enabled and season is selected
  useEffect(() => {
    // Clear selected coach when season changes
    setSelectedAdminCoach(null);
    if (onAdminCoachChange) {
      onAdminCoachChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    
    if (!adminRoleEnabled || !isAdmin || !selectedSeason) {
      setAvailableCoaches([]);
      return;
    }

    const loadCoaches = async () => {
      try {
        // Get distinct coaches from runner_season_info for the selected season
        // Use selectedSeason directly (it should already be in the correct format like "Season 13")
        console.log('Loading coaches for season:', selectedSeason);
        
        const { data, error } = await supabase
          .from('runner_season_info')
          .select('coach')
          .eq('season', selectedSeason);
        
        if (error) {
          console.error('Error loading coaches:', error);
          console.error('Error details:', error.message, error.details);
          setAvailableCoaches([]);
          return;
        }
        
        console.log('Coaches data from runner_season_info:', data);
        
        if (data && data.length > 0) {
          // Get distinct coach names and sort them
          const distinctCoaches = [...new Set(data.map(item => item.coach).filter(Boolean))].sort((a, b) => 
            a.localeCompare(b)
          );
          
          console.log('Distinct coaches:', distinctCoaches);
          
          // Store coach names directly (no need to lookup email_id)
          setAvailableCoaches(distinctCoaches);
        } else {
          console.log('No coaches found for season:', selectedSeason);
          setAvailableCoaches([]);
        }
      } catch (error) {
        console.error('Error in loadCoaches:', error);
        setAvailableCoaches([]);
      }
    };

    loadCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminRoleEnabled, isAdmin, selectedSeason]);

  // Refresh pending status when cohort data changes (in case status was updated elsewhere)
  useEffect(() => {
    if (cohortData.length > 0) {
      fetchPendingActionRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortData]);

  // Function to refresh pending status (can be called from parent components)
  // eslint-disable-next-line no-unused-vars
  const refreshPendingStatus = () => {
    fetchPendingActionRequests();
  };

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown') && !event.target.closest('.search-container')) {
        setSeasonMenuOpen(false);
        setDistanceMenuOpen(false);
        setShowAutocomplete(false);
        setCoachesMenuOpen(false);
      }
      if (!event.target.closest('.runner-menu')) {
        setMenuOpenFor(null);
      }
      if (!event.target.closest('.transfer-level-dropdown')) {
        setTransferLevelMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listen for notes updates to refresh the star colors
  useEffect(() => {
    const handleNotesUpdated = (event) => {
      // Trigger a page refresh or data reload to update the star colors
      // For now, we'll just log the event - the parent component will handle the data refresh
      console.log('Notes updated:', event.detail);
    };

    window.addEventListener('notesUpdated', handleNotesUpdated);
    return () => window.removeEventListener('notesUpdated', handleNotesUpdated);
  }, []);
  // Filter cohort data based on selected distance and search term
  const filteredRunners = cohortData.filter(runner => {
    const matchesDistance = selectedDistance === 'All' || runner.race_distance === selectedDistance;
    const matchesSearch = !searchTerm || 
      runner.runner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      runner.email_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesDistance && matchesSearch;
  });

  // Get runner names for autocomplete (filtered by current distance)
  const runnerNames = cohortData
    .filter(runner => selectedDistance === 'All' || runner.race_distance === selectedDistance)
    .map(runner => runner.runner_name || runner.email_id)
    .filter(Boolean);

  // Get autocomplete suggestions
  const autocompleteSuggestions = runnerNames.filter(name =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10);

  // Handle autocomplete selection
  const handleAutocompleteSelect = (name) => {
    setSearchTerm(name);
    setShowAutocomplete(false);
  };

  // Handle accordion section toggle
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
    
    // If opening metrics section and we have a selected runner, fetch the data
    if (sectionName === 'metrics' && selectedRunner && currentSeason && coachEmail) {
      fetchSeasonMetrics(selectedRunner.email_id);
    }
    
    // If opening race timings section and we have a selected runner, fetch the data
    if (sectionName === 'raceTimings' && selectedRunner && selectedSeason) {
      fetchRaceTimings(selectedRunner.email_id);
    }
  };
  
  // Validation function for overridden meso score
  const validateOverriddenScore = (value) => {
    if (value === '' || value === null || value === undefined) {
      return { isValid: true, message: '' }; // Allow empty values
    }
    
    const numValue = parseFloat(value);
    
    // Check if it's a valid number
    if (isNaN(numValue)) {
      return { isValid: false, message: 'Please enter a valid number.' };
    }
    
    // Check if it's between 1 and 5
    if (numValue < 1 || numValue > 5) {
      return { isValid: false, message: 'Score must be between 1 and 5.' };
    }
    
    // Check if it has at most one decimal place
    const decimalPlaces = value.toString().split('.')[1]?.length || 0;
    if (decimalPlaces > 1) {
      return { isValid: false, message: 'Score can have at most one decimal place.' };
    }
    
    return { isValid: true, message: '' };
  };

  // Validation function for race timings (HH:MM:SS format)
  const validateRaceTimings = (value) => {
    if (value === '' || value === null || value === undefined) {
      return { isValid: true, message: '' }; // Allow empty values
    }
    
    // Check if it matches HH:MM:SS format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    if (!timeRegex.test(value)) {
      return { isValid: false, message: 'Please enter time in HH:MM:SS format (e.g., 01:30:45).' };
    }
    
    return { isValid: true, message: '' };
  };

  // Fetch season metrics for a specific runner
  const fetchSeasonMetrics = async (runnerEmail) => {
    if (!runnerEmail || !currentSeason || !coachEmail) return;
    
    setMetricsLoading(true);
    
    // Get season filter
    const seasonFilter = currentSeason.toString().startsWith('Season ') ? currentSeason : `Season ${currentSeason}`;
    
    try {
      // Execute query to fetch meso metrics
      const { data, error } = await supabase
        .from('rhwb_coach_input')
        .select('*')
        .eq('coach_email', coachEmail)
        .eq('season', seasonFilter)
        .eq('email_id', runnerEmail)
        .limit(1);
      
      if (error) {
        console.error('Error fetching season metrics:', error);
        setSeasonMetrics(null);
      } else {
        setSeasonMetrics(data);
      }
    } catch (error) {
      console.error('Error in fetchSeasonMetrics:', error);
      setSeasonMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Fetch race timings for a specific runner
  const fetchRaceTimings = async (runnerEmail) => {
    if (!runnerEmail || !selectedSeason) return;
    
    setRaceTimingsLoading(true);
    
    // Get season filter
    const seasonFilter = selectedSeason.toString().startsWith('Season ') ? selectedSeason : `Season ${selectedSeason}`;
    
    try {
      const { data, error } = await supabase
        .from('runner_season_info')
        .select('race_timings, race_pr, race_comments, race_distance, race_distance_completed')
        .eq('email_id', runnerEmail)
        .eq('season', seasonFilter)
        .single();
      
      if (error) {
        console.error('Error fetching race timings:', error);
        setRaceTimings(null);
      } else {
        setRaceTimings(data);
        // Initialize form data with current values
        setRaceTimingsFormData({
          race_timings: data?.race_timings || '',
          race_pr: data?.race_pr === true || data?.race_pr === 'true' || false,
          race_comments: data?.race_comments || '',
          race_distance_completed: data?.race_distance_completed || data?.race_distance || ''
        });
      }
    } catch (error) {
      console.error('Error in fetchRaceTimings:', error);
      setRaceTimings(null);
    } finally {
      setRaceTimingsLoading(false);
    }
  };

  // Fetch recent activities for a runner (used to populate race timings)
  const fetchRecentActivities = async (runnerEmail, offset = 0) => {
    if (!runnerEmail || !selectedSeason) return;

    setRecentActivitiesLoading(true);

    // Extract numeric season number from "Season 15" format
    const seasonStr = selectedSeason.toString();
    const seasonNo = seasonStr.startsWith('Season ')
      ? parseInt(seasonStr.replace('Season ', ''), 10)
      : parseInt(seasonStr, 10);

    if (isNaN(seasonNo)) {
      setRecentActivitiesLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('rhwb_activities')
        .select('workout_date, completed_distance, moving_time, completed_time')
        .eq('email_id', runnerEmail.toLowerCase())
        .eq('season_no', seasonNo)
        .in('activity', ['RUN', 'WALK', 'LONG-RUN'])
        .not('completed_distance', 'is', null)
        .order('workout_date', { ascending: false })
        .range(offset, offset + 4);

      if (error) {
        console.error('Error fetching recent activities:', error);
      } else {
        const rows = data || [];
        setRecentActivities(prev => offset === 0 ? rows : [...prev, ...rows]);
        setRecentActivitiesOffset(offset + rows.length);
        setRecentActivitiesHasMore(rows.length === 5);
      }
    } catch (error) {
      console.error('Error in fetchRecentActivities:', error);
    } finally {
      setRecentActivitiesLoading(false);
    }
  };

  // Handle edit button click
  // eslint-disable-next-line no-unused-vars
  const handleEditMeso = (mesoData) => {
    setEditingMeso(mesoData.meso);
    setEditFormData({
      meso_score_override: mesoData.meso_score_override || '',
      meso_qual_score: mesoData.meso_qual_score || ''
    });
    setEditValidationErrors({});
  };

  // Handle edit form changes
  // eslint-disable-next-line no-unused-vars
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (editValidationErrors[field]) {
      setEditValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Validate overridden score in real-time
    if (field === 'meso_score_override') {
      const validation = validateOverriddenScore(value);
      if (!validation.isValid) {
        setEditValidationErrors(prev => ({ ...prev, [field]: validation.message }));
      }
    }
  };

  // Handle save edit
  // eslint-disable-next-line no-unused-vars
  const handleSaveEdit = async () => {
    // Validate before saving
    const overrideValidation = validateOverriddenScore(editFormData.meso_score_override);
    if (!overrideValidation.isValid) {
      setEditValidationErrors(prev => ({ ...prev, meso_score_override: overrideValidation.message }));
      return;
    }

    try {
      // Get the season filter for the update query
      const seasonFilter = currentSeason.toString().startsWith('Season ') ? currentSeason : `Season ${currentSeason}`;

      // Update override score in Supabase (non-sensitive)
      const { error } = await supabase
        .from('rhwb_coach_input')
        .update({
          meso_score_override: editFormData.meso_score_override || null
        })
        .eq('coach_email', coachEmail)
        .eq('season', seasonFilter)
        .eq('email_id', selectedRunner.email_id)
        .eq('meso', editingMeso);

      if (error) {
        console.error('Error updating meso metrics:', error);
        setEditMessage({ type: 'error', text: 'Failed to update metrics. Please try again.' });
        return;
      }

      // Write qual score to Cloud SQL (HIPAA-sensitive)
      if (editFormData.meso_qual_score) {
        await upsertQualScore(selectedRunner.email_id, seasonFilter, editingMeso, editFormData.meso_qual_score);
      }

      // Update local state
      setSeasonMetrics(prev =>
        prev?.map(metric =>
          metric.meso === editingMeso
            ? {
                ...metric,
                meso_score_override: editFormData.meso_score_override || null,
                meso_qual_score: editFormData.meso_qual_score || null
              }
            : metric
        )
      );

      // Reset edit state
      setEditingMeso(null);
      setEditFormData({});
      setEditValidationErrors({});

      // Show success message
      setEditMessage({ type: 'success', text: 'Metrics updated successfully!' });

      // Clear message after 3 seconds
      setTimeout(() => {
        setEditMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Error in handleSaveEdit:', error);
      setEditMessage({ type: 'error', text: 'Failed to update metrics. Please try again.' });
    }
  };

  // Handle cancel edit
  // eslint-disable-next-line no-unused-vars
  const handleCancelEdit = () => {
    setEditingMeso(null);
    setEditFormData({});
    setEditValidationErrors({});
    setEditMessage({ type: '', text: '' });
  };

  // Handle race timings edit
  const handleEditRaceTimings = () => {
    setEditingRaceTimings(true);
    setRaceTimingsMessage({ type: '', text: '' });
    setRecentActivities([]);
    setRecentActivitiesOffset(0);
    setRecentActivitiesHasMore(false);
    if (selectedRunner?.email_id) {
      fetchRecentActivities(selectedRunner.email_id);
    }
  };

  // Handle race timings form changes
  const handleRaceTimingsFormChange = (field, value) => {
    setRaceTimingsFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate race timings field
    if (field === 'race_timings') {
      const validation = validateRaceTimings(value);
      setRaceTimingsValidation(prev => ({ ...prev, race_timings: validation.isValid ? '' : validation.message }));
    }
  };

  // Handle save race timings
  const handleSaveRaceTimings = async () => {
    if (!selectedRunner || !selectedSeason) return;

    // Validate race timings before saving
    const timingsValidation = validateRaceTimings(raceTimingsFormData.race_timings);
    if (!timingsValidation.isValid) {
      setRaceTimingsValidation(prev => ({ ...prev, race_timings: timingsValidation.message }));
      return;
    }

    try {
      const seasonFilter = selectedSeason.toString().startsWith('Season ') ? selectedSeason : `Season ${selectedSeason}`;
      
      // Check if record exists
      const { data: existingData } = await supabase
        .from('runner_season_info')
        .select('email_id')
        .eq('email_id', selectedRunner.email_id)
        .eq('season', seasonFilter)
        .single();

      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('runner_season_info')
          .update({
            race_timings: raceTimingsFormData.race_timings || null,
            race_pr: raceTimingsFormData.race_pr || null,
            race_comments: raceTimingsFormData.race_comments || null,
            race_distance_completed: raceTimingsFormData.race_distance_completed || null
          })
          .eq('email_id', selectedRunner.email_id)
          .eq('season', seasonFilter);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('runner_season_info')
          .insert([{
            email_id: selectedRunner.email_id,
            season: seasonFilter,
            race_timings: raceTimingsFormData.race_timings || null,
            race_pr: raceTimingsFormData.race_pr || null,
            race_comments: raceTimingsFormData.race_comments || null,
            race_distance_completed: raceTimingsFormData.race_distance_completed || null
          }]);

        if (error) throw error;
      }

      // Update local state
      setRaceTimings({
        race_timings: raceTimingsFormData.race_timings,
        race_pr: raceTimingsFormData.race_pr,
        race_comments: raceTimingsFormData.race_comments,
        race_distance_completed: raceTimingsFormData.race_distance_completed
      });

      // Reset edit state
      setEditingRaceTimings(false);
      setRecentActivities([]);
      setRecentActivitiesOffset(0);
      setRecentActivitiesHasMore(false);
      setRaceTimingsMessage({ type: 'success', text: 'Race timings updated successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setRaceTimingsMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Error saving race timings:', error);
      setRaceTimingsMessage({ type: 'error', text: 'Failed to update race timings. Please try again.' });
    }
  };

  // Handle cancel race timings edit
  const handleCancelRaceTimingsEdit = () => {
    setEditingRaceTimings(false);
    setRaceTimingsMessage({ type: '', text: '' });
    setRaceTimingsValidation({ race_timings: '' });
    setRecentActivities([]);
    setRecentActivitiesOffset(0);
    setRecentActivitiesHasMore(false);
    // Reset form data to original values
    setRaceTimingsFormData({
      race_timings: raceTimings?.race_timings || '',
      race_pr: raceTimings?.race_pr === true || raceTimings?.race_pr === 'true' || false,
      race_comments: raceTimings?.race_comments || '',
      race_distance_completed: raceTimings?.race_distance_completed || raceTimings?.race_distance || ''
    });
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, type, runnerId) => {
    try {
      await navigator.clipboard.writeText(text);
      const key = `${runnerId}-${type}`;
      setCopiedItems(prev => ({ ...prev, [key]: true }));

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedItems(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Generate and download VCF file
  const downloadVCF = (runner) => {
    const firstName = runner.first_name || '';
    const lastName = runner.last_name || '';
    const fullName = runner.runner_name || `${firstName} ${lastName}`.trim() || 'Unknown';

    // Create VCF content
    const vcfContent = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${fullName}`,
      `N:${lastName};${firstName};;;`,
      `TEL;TYPE=CELL:${runner.phone_no || ''}`,
      'ORG:RHWB',
      'END:VCARD'
    ].join('\n');

    // Create blob and download
    const blob = new Blob([vcfContent], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fullName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Handle runner selection
  const handleRunnerSelect = (runner) => {
    // Toggle selection - if clicking the same runner, deselect it
    if (selectedRunner?.email_id === runner.email_id) {
      setSelectedRunner(null);
      setExpandedSections({});
      setSeasonMetrics(null); // Clear metrics when deselecting
    } else {
      setSelectedRunner(runner);
      // Reset expanded sections when selecting a new runner
      setExpandedSections({});
      setSeasonMetrics(null); // Clear previous runner's metrics
    }
    // Always clear recent activities when switching runners
    setEditingRaceTimings(false);
    setRecentActivities([]);
    setRecentActivitiesOffset(0);
    setRecentActivitiesHasMore(false);
  };

  // Handle menu toggle
  const toggleMenu = (runnerId, event) => {
    event.stopPropagation(); // Prevent triggering runner selection
    setMenuOpenFor(menuOpenFor === runnerId ? null : runnerId);
  };

  const normalizeActivity = (activityValue) => {
    if (!activityValue) return null;
    const v = String(activityValue).trim().toLowerCase();
    if (!v) return null;
    if (v.startsWith('walk')) return 'Walk';
    if (v.startsWith('run')) return 'Run';
    return 'Run';
  };

  const normalizeSegment = (segmentValue) => {
    if (!segmentValue) return null;
    const v = String(segmentValue).trim().toLowerCase();
    if (!v) return null;
    if (v === 'lite') return 'Lite';
    if (v === 'pro') return 'Pro';
    if (v === 'masters') return 'Masters';
    return null;
  };

  // Handle transfer runner
  const handleTransferRunner = async (runner) => {
    setTransferRunner(runner);
    setMenuOpenFor(null); // Close the menu
    
    // Fetch the current level from runner_season_info
    if (runner?.email_id && selectedSeason) {
      try {
        const seasonFilter = selectedSeason.toString().startsWith('Season ') ? selectedSeason : `Season ${selectedSeason}`;
        const { data, error } = await supabase
          .from('runner_season_info')
          .select('level, activity, segment')
          .eq('email_id', runner.email_id)
          .eq('season', seasonFilter)
          .single();
        
        if (!error && data) {
          setTransferRunnerLevel(data.level || null);
          const currentActivity = normalizeActivity(data.activity);
          setTransferRunnerActivity(currentActivity);
          setSelectedNewActivity(currentActivity);
          const currentSegment = normalizeSegment(data.segment);
          setTransferRunnerSegment(currentSegment);
          setSelectedNewSegment(currentSegment);
        } else {
          setTransferRunnerLevel(null);
          setTransferRunnerActivity(null);
          setSelectedNewActivity(null);
          setTransferRunnerSegment(null);
          setSelectedNewSegment(null);
        }
      } catch (error) {
        console.error('Error fetching runner level:', error);
        setTransferRunnerLevel(null);
        setTransferRunnerActivity(null);
        setSelectedNewActivity(null);
        setTransferRunnerSegment(null);
        setSelectedNewSegment(null);
      }
    } else {
      setTransferRunnerLevel(null);
      setTransferRunnerActivity(null);
      setSelectedNewActivity(null);
      setTransferRunnerSegment(null);
      setSelectedNewSegment(null);
    }
    
    setShowTransferModal(true);
  };

  // Handle defer runner
  const handleDeferRunner = (runner) => {
    setDeferRunner(runner);
    setShowDeferModal(true);
    setMenuOpenFor(null); // Close the menu
  };

  // Handle star click (opens notes modal)
  const handleStarClick = async (runner, event) => {
    event.stopPropagation(); // Prevent triggering runner selection
    
    // Select the runner if not already selected
    if (selectedRunner?.email_id !== runner.email_id) {
      setSelectedRunner(runner);
      setExpandedSections({});
    }
    
    // Expand the coach notes section
    setExpandedSections(prev => ({
      ...prev,
      coachNotes: true
    }));
  };

  // Auto-save notes
  const saveNotes = async (content) => {
    if (!notesRunner || !coachEmail) return;
    try {
      setNotesLoading(true);
      // Check if notes already exist for this runner/coach combination
      const { data: existingNotes } = await supabase
        .from('profile_notes')
        .select('note_ts')
        .eq('email_id', notesRunner.email_id)
        .eq('comment_by', coachEmail)
        .order('note_ts', { ascending: false })
        .limit(1)
        .single();
      if (existingNotes) {
        // Update existing note (by timestamp)
        const { error } = await supabase
          .from('profile_notes')
          .update({
            note: content,
            note_ts: new Date().toISOString()
          })
          .eq('email_id', notesRunner.email_id)
          .eq('comment_by', coachEmail)
          .eq('note_ts', existingNotes.note_ts);
        if (error) throw error;
      } else {
        // Insert new note
        const { error } = await supabase
          .from('profile_notes')
          .insert([{
            email_id: notesRunner.email_id,
            note: content,
            note_ts: new Date().toISOString(),
            comment_by: coachEmail
          }]);
        if (error) throw error;
      }
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
      // Update notes_present in runners_profile
      if (notesRunner && notesRunner.email_id) {
        const { error: updateError } = await supabase
          .from('runners_profile')
          .update({ notes_present: true })
          .eq('email_id', notesRunner.email_id);
        if (updateError) {
          console.error('Failed to update notes_present in runners_profile:', updateError);
        }
      }
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  // Handle notes content change with auto-save
  const handleNotesChange = (content) => {
    setNotesContent(content);
    // Auto-save after a short delay
    setTimeout(() => {
      saveNotes(content);
    }, 1000); // 1 second delay
  };

  // Handle program selection (updates selected program in transfer modal)
  const handleProgramSelection = (newProgram) => {
    setSelectedProgram(newProgram);
    // Reset level when program changes (Lite doesn't have levels)
    if (newProgram === 'Lite') {
      setSelectedNewLevel(null);
    }
  };

  // Handle submit button click (shows confirmation modal)
  const handleTransferSubmit = () => {
    if (!selectedProgram) {
      alert('Please select a program first.');
      return;
    }
    if (!selectedNewActivity) {
      alert('Please select a new activity (Run or Walk).');
      return;
    }
    if (!selectedNewSegment) {
      alert('Please select a new segment (Lite, Pro, or Masters).');
      return;
    }
    // For race distance programs, require level selection
    if (selectedProgram !== 'Lite' && ['5K', '10K', 'Half Marathon', 'Full Marathon'].includes(selectedProgram) && !selectedNewLevel) {
      alert('Please select a level for the new program.');
      return;
    }
    setShowTransferModal(false);
    setTransferDistanceMenuOpen(false);
    setTransferLevelMenuOpen(false);
    setShowConfirmationModal(true);
  };

  const handleDeferSubmit = () => {
    setShowDeferModal(false);
    setShowDeferConfirmationModal(true);
  };

  // Handle confirmed program transfer
  const handleConfirmedTransfer = async () => {
    try {
      // Check authentication
      await supabase.auth.getSession();

      // Build transfer data WITHOUT comments (comments go to Cloud SQL)
      const transferData = {
        action_type: 'Transfer Runner',
        runner_email_id: transferRunner.email_id,
        requestor_email_id: coachEmail || 'unknown@example.com',
        latest_row: true,
        current_race_distance: transferRunner.race_distance,
        current_program_level: transferRunnerLevel || null,
        current_activity: transferRunnerActivity || null,
        current_segment: transferRunnerSegment || null,
        new_race_distance: selectedProgram,
        new_program_level: selectedNewLevel || null,
        new_activity: selectedNewActivity || null,
        new_segment: selectedNewSegment || null,
        season: selectedSeason
      };

      // First, let's test if we can read from the table
      const { error: testError } = await supabase
        .from('rhwb_action_requests')
        .select('*')
        .limit(1);

      if (testError) {
        console.error('Failed to read from table:', testError);
        alert('Cannot connect to table. Check Supabase configuration.');
        return;
      }

      // Insert into rhwb_action_requests table (without comments)
      const { data: insertedData, error } = await supabase
        .from('rhwb_action_requests')
        .insert([transferData])
        .select('id');

      if (error) {
        console.error('Failed to insert transfer request:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        throw error;
      }

      // Write comment to Cloud SQL if provided
      const commentText = transferComment.trim();
      if (commentText && insertedData?.[0]?.id) {
        try {
          await upsertActionComment(
            insertedData[0].id,
            transferRunner.email_id,
            selectedSeason,
            commentText,
            'Transfer Runner'
          );
        } catch (commentError) {
          console.error('Failed to save comment to Cloud SQL:', commentError);
          // Don't fail the whole transfer if comment save fails
        }
      }

      // Refresh pending status from database
      await fetchPendingActionRequests();

      // Close modals and reset state
      setShowConfirmationModal(false);
      setTransferRunner(null);
      setTransferRunnerLevel(null);
      setTransferRunnerActivity(null);
      setTransferRunnerSegment(null);
      setSelectedProgram(null);
      setSelectedNewLevel(null);
      setSelectedNewActivity(null);
      setSelectedNewSegment(null);
      setTransferComment('');
      setTransferDistanceMenuOpen(false);
      setTransferLevelMenuOpen(false);

    } catch (error) {
      console.error('Failed to submit transfer request:', error);
      alert('Failed to submit transfer request. Check console for details.');
    }
  };

  // Handle confirmed defer request
  const handleConfirmedDefer = async () => {
    try {
      // Check authentication
      await supabase.auth.getSession();

      // Build defer data WITHOUT comments (comments go to Cloud SQL)
      const deferData = {
        action_type: 'Defer Runner',
        runner_email_id: deferRunner.email_id,
        requestor_email_id: coachEmail || 'unknown@example.com',
        latest_row: true,
        status: 'pending',
        season: selectedSeason
      };

      // First, let's test if we can read from the table
      const { error: testError } = await supabase
        .from('rhwb_action_requests')
        .select('*')
        .limit(1);

      if (testError) {
        console.error('Failed to read from table:', testError);
        alert('Cannot connect to table. Check Supabase configuration.');
        return;
      }

      // Insert into rhwb_action_requests table (without comments)
      const { data: insertedData, error } = await supabase
        .from('rhwb_action_requests')
        .insert([deferData])
        .select('id');

      if (error) {
        console.error('Failed to insert defer request:', error);
        console.error('Error details:', error.message, error.details, error.hint);
        throw error;
      }

      // Write comment to Cloud SQL if provided
      const commentText = deferComment.trim();
      if (commentText && insertedData?.[0]?.id) {
        try {
          await upsertActionComment(
            insertedData[0].id,
            deferRunner.email_id,
            selectedSeason,
            commentText,
            'Defer Runner'
          );
        } catch (commentError) {
          console.error('Failed to save comment to Cloud SQL:', commentError);
          // Don't fail the whole defer if comment save fails
        }
      }

      // Refresh pending status from database
      await fetchPendingActionRequests();

      // Close modals and reset state
      setShowDeferConfirmationModal(false);
      setDeferRunner(null);
      setDeferComment('');
      
    } catch (error) {
      console.error('Failed to submit defer request:', error);
      alert('Failed to submit defer request. Check console for details.');
    }
  };

  const distanceOptions = filterOptions.distances;

  // Show loading state
  if (cohortLoading) {
    return <div className="text-center py-8">Loading runner data...</div>;
  }

  // Show error state
  if (cohortError) {
    return <div className="text-center py-8 text-red-600">{cohortError}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Filters */}
      <div className="mb-6 sm:mb-8 relative z-10">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col gap-3 sm:gap-4">
            {/* Filter Chips */}
            <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
              {/* Season Chip */}
              <div className="relative filter-dropdown z-50">
                <button
                  onClick={() => setSeasonMenuOpen(!seasonMenuOpen)}
                  className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-purple-50 text-purple-700 rounded-full font-medium hover:bg-purple-100 transition-colors duration-200 border border-purple-200 text-sm sm:text-base"
                >
                  <span>{selectedSeason || 'Select Season'}</span>
                  <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200 ${seasonMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {seasonMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 min-w-[150px] max-h-[300px] overflow-y-auto">
                    {availableSeasons.map((season) => (
                      <button
                        key={season.season}
                        onClick={() => {
                          setSelectedSeason(season.season);
                          setSeasonMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                          selectedSeason === season.season ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {season.season}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Coaches Dropdown - Only shown when admin toggle is enabled */}
              {adminRoleEnabled && (
                <div className="relative filter-dropdown z-50">
                  <button
                    onClick={() => {
                      if (!selectedSeason) {
                        alert('Please select a season first');
                        return;
                      }
                      setCoachesMenuOpen(!coachesMenuOpen);
                    }}
                    disabled={!selectedSeason || availableCoaches.length === 0}
                    className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-full font-medium transition-colors duration-200 border text-sm sm:text-base ${
                      !selectedSeason || availableCoaches.length === 0
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200'
                    }`}
                  >
                    <span>
                      {!selectedSeason 
                        ? 'Select Season First' 
                        : selectedAdminCoach 
                          ? selectedAdminCoach
                          : availableCoaches.length === 0
                            ? 'No Coaches'
                            : 'Select Coach'
                      }
                    </span>
                    <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200 ${coachesMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {coachesMenuOpen && selectedSeason && availableCoaches.length > 0 && (
                    <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 min-w-[200px] max-h-[300px] overflow-y-auto">
                      {availableCoaches.map((coachName) => (
                        <button
                          key={coachName}
                          onClick={() => {
                            setSelectedAdminCoach(coachName);
                            setCoachesMenuOpen(false);
                            // Notify parent component of coach change (pass coach name)
                            if (onAdminCoachChange) {
                              onAdminCoachChange(coachName);
                            }
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                            selectedAdminCoach === coachName ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {coachName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Race Distance Chip */}
              <div className="relative filter-dropdown z-40">
                <button
                  onClick={() => setDistanceMenuOpen(!distanceMenuOpen)}
                  className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-blue-50 text-blue-700 rounded-full font-medium hover:bg-blue-100 transition-colors duration-200 border border-blue-200 text-sm sm:text-base"
                >
                  <span>{selectedDistance}</span>
                  <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200 ${distanceMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {distanceMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 min-w-[120px]">
                    {distanceOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedDistance(option.value);
                          setDistanceMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                          selectedDistance === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Bar with Autocomplete */}
              <div className="relative search-container flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search runners..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowAutocomplete(true);
                  }}
                  onFocus={() => setShowAutocomplete(true)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
                
                {showAutocomplete && autocompleteSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    {autocompleteSuggestions.map((name, index) => (
                      <button
                        key={index}
                        onClick={() => handleAutocompleteSelect(name)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Admin Role Toggle - Only visible for admin users */}
              {isAdmin && (
                <div className="flex items-center gap-2 ml-auto">
                  <label className="text-sm font-medium text-gray-700">Admin Role</label>
                  <button
                    type="button"
                    onClick={() => {
                      const newValue = !adminRoleEnabled;
                      if (adminRoleEnabledProp === undefined) {
                        setAdminRoleEnabledLocal(newValue);
                      }
                      if (onAdminRoleToggle) {
                        onAdminRoleToggle(newValue);
                      }
                      if (!newValue) {
                        setSelectedAdminCoach(null);
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      adminRoleEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={adminRoleEnabled}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        adminRoleEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* My Cohort Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center mb-6">
          <Users className="h-6 w-6 text-blue-600 mr-3" />
          <h2 className="text-2xl font-bold text-gray-900">My Cohort</h2>
        </div>
        
        {/* Results Summary */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <p className="text-blue-700">
            Found <span className="font-semibold">{filteredRunners.length}</span> runners in your cohort
            {currentSeason && ` for ${currentSeason}`}
            {selectedDistance !== 'All' && ` in ${selectedDistance}`}
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>

        {/* Cohort List */}
        {filteredRunners.length > 0 ? (
          <div className="space-y-3">
            {filteredRunners.map((runner, index) => (
              <div key={runner.email_id || index} className="space-y-3">
                {/* Runner Card - Clickable Header */}
                <div 
                  className={`bg-white rounded-lg sm:rounded-xl border border-gray-200 p-3 sm:p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer ${
                    selectedRunner?.email_id === runner.email_id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => handleRunnerSelect(runner)}
                >
                  <div className="flex items-center justify-between">
                    {/* Left side - Avatar and basic info */}
                    <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                      {/* Avatar */}
                      <div className="bg-blue-100 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-semibold text-xs sm:text-sm">
                          {runner.runner_name ? runner.runner_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                        </span>
                      </div>
                      
                      {/* Runner info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
                          <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">{runner.runner_name}</h3>
                          
                          {/* Pending Transfer/Defer Status */}
                          {(pendingTransfers.has(runner.email_id) || pendingDefers.has(runner.email_id)) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                              {pendingTransfers.has(runner.email_id) ? 'Pending Transfer' : 'Pending Defer'}
                            </span>
                          )}
                          
                          {/* Desktop: Show age, phone, and location */}
                          <span className="hidden sm:inline text-xs sm:text-sm text-gray-500">{runner.gender_age || 'N/A'}</span>
                          {runner.phone_no && (
                            <div className="hidden sm:flex items-center text-xs sm:text-sm text-gray-500 space-x-2">
                              <div className="flex items-center">
                                <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                <a 
                                  href={`tel:${runner.phone_no}`}
                                  className="hover:text-blue-600 hover:underline transition-colors"
                                  title="Click to call"
                                >
                                  {runner.phone_no}
                                </a>
                              </div>
                              <a 
                                href={`https://wa.me/${runner.phone_no.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-700 transition-colors"
                                title="Open WhatsApp"
                              >
                                <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                              </a>
                              <button
                                onClick={() => copyToClipboard(runner.phone_no, 'phone', runner.email_id)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Copy phone number"
                              >
                                {copiedItems[`${runner.email_id}-phone`] ? (
                                  <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                                ) : (
                                  <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => downloadVCF(runner)}
                                className="text-blue-500 hover:text-blue-700 transition-colors"
                                title="Add to Contacts"
                              >
                                <UserPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1">
                          {/* Email - Desktop only */}
                          {runner.email_id && (
                            <div className="hidden sm:flex items-center text-xs sm:text-sm text-gray-500 space-x-2">
                              <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                              <a 
                                href={`mailto:${runner.email_id}`}
                                className="hover:text-blue-600 hover:underline transition-colors"
                                title="Send email"
                              >
                                {runner.email_id}
                              </a>
                              <button
                                onClick={() => copyToClipboard(runner.email_id, 'email', runner.email_id)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Copy email address"
                              >
                                {copiedItems[`${runner.email_id}-email`] ? (
                                  <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                                ) : (
                                  <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                                )}
                              </button>
                            </div>
                          )}
                          
                          {/* Race Distance Chip */}
                          <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                            runner.race_distance === '5K' ? 'bg-green-100 text-green-700' :
                            runner.race_distance === '10K' ? 'bg-blue-100 text-blue-700' :
                            runner.race_distance === 'Half Marathon' ? 'bg-orange-100 text-orange-700' :
                            runner.race_distance === 'Full Marathon' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {/* Mobile: Show abbreviated version */}
                            <span className="sm:hidden">
                              {runner.race_distance === 'Half Marathon' ? 'HM' :
                               runner.race_distance === 'Full Marathon' ? 'FM' :
                               runner.race_distance}
                            </span>
                            {/* Desktop: Show full version */}
                            <span className="hidden sm:inline">
                              {runner.race_distance}
                            </span>
                          </span>

                          {/* Contact Icons - Mobile Only */}
                          {runner.phone_no && (
                            <div className="flex sm:hidden items-center space-x-2">
                              <a
                                href={`tel:${runner.phone_no}`}
                                className="text-blue-500 hover:text-blue-700 transition-colors"
                                title="Click to call"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                              </a>
                              <a
                                href={`https://wa.me/${runner.phone_no.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-700 transition-colors"
                                title="Open WhatsApp"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MessageCircle className="h-4 w-4" />
                              </a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadVCF(runner);
                                }}
                                className="text-blue-500 hover:text-blue-700 transition-colors"
                                title="Add to Contacts"
                              >
                                <UserPlus className="h-4 w-4" />
                              </button>
                            </div>
                          )}

                          {/* Location - Desktop only */}
                          {runner.location && (
                            <div className="hidden sm:flex items-center text-xs sm:text-sm text-gray-500">
                              <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              {runner.location}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right side - Action icons */}
                    <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-2">
                      {/* Star icon (opens notes) */}
                      <button 
                        className={`p-1.5 sm:p-2 transition-colors ${runner.notes_present ? 'text-green-500 hover:text-green-600' : 'text-gray-400 hover:text-yellow-500'}`}
                        onClick={(e) => handleStarClick(runner, e)}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </button>
                      
                      {/* More options */}
                      <div className="relative runner-menu">
                        <button 
                          className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={(e) => toggleMenu(runner.email_id, e)}
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>
                        
                        {/* Dropdown Menu */}
                        {menuOpenFor === runner.email_id && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-[140px] sm:min-w-[160px]">
                            <button
                              onClick={() => handleTransferRunner(runner)}
                              disabled={pendingTransfers.has(runner.email_id)}
                              className={`w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm transition-colors flex items-center space-x-2 ${
                                pendingTransfers.has(runner.email_id)
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              <span>{pendingTransfers.has(runner.email_id) ? 'Transfer Pending' : 'Transfer Runner'}</span>
                            </button>
                            
                            <button
                              onClick={() => handleDeferRunner(runner)}
                              disabled={pendingDefers.has(runner.email_id)}
                              className={`w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm transition-colors flex items-center space-x-2 ${
                                pendingDefers.has(runner.email_id)
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>{pendingDefers.has(runner.email_id) ? 'Defer Pending' : 'Defer Runner'}</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Chevron down */}
                      <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 transition-transform duration-200 ${
                        selectedRunner?.email_id === runner.email_id ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>
                </div>

                {/* Accordion Sections - Only show when runner is selected */}
                {selectedRunner?.email_id === runner.email_id && (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 sm:p-6 mt-4">
                    <div className="space-y-2 sm:space-y-3">
                    {/* Bio - Mobile Only */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden sm:hidden">
                      <button
                        onClick={() => toggleSection('bio')}
                        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium text-gray-900 text-sm sm:text-base">Bio</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                          expandedSections.bio ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections.bio && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-3">
                          {/* Contact Information */}
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 text-sm">Contact Information</h4>
                            <div className="space-y-2">
                              {/* Age/Gender */}
                              {runner.gender_age && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  <span>{runner.gender_age}</span>
                                </div>
                              )}

                              {/* Phone Number */}
                              {runner.phone_no && (
                                <div className="flex items-center text-sm text-gray-600 space-x-2">
                                  <div className="flex items-center">
                                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                    </svg>
                                    <a
                                      href={`tel:${runner.phone_no}`}
                                      className="hover:text-blue-600 hover:underline transition-colors"
                                      title="Click to call"
                                    >
                                      {runner.phone_no}
                                    </a>
                                  </div>
                                  <a
                                    href={`https://wa.me/${runner.phone_no.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-700 transition-colors"
                                    title="Open WhatsApp"
                                  >
                                    <MessageCircle className="h-4 w-4" />
                                  </a>
                                  <button
                                    onClick={() => copyToClipboard(runner.phone_no, 'phone', runner.email_id)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy phone number"
                                  >
                                    {copiedItems[`${runner.email_id}-phone`] ? (
                                      <Check className="h-4 w-4" />
                                    ) : (
                                      <Copy className="h-4 w-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => downloadVCF(runner)}
                                    className="text-blue-500 hover:text-blue-700 transition-colors"
                                    title="Add to Contacts"
                                  >
                                    <UserPlus className="h-4 w-4" />
                                  </button>
                                </div>
                              )}

                              {/* Email */}
                              {runner.email_id && (
                                <div className="flex items-center text-sm text-gray-600 space-x-2">
                                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                                  <a
                                    href={`mailto:${runner.email_id}`}
                                    className="hover:text-blue-600 hover:underline transition-colors"
                                    title="Send email"
                                  >
                                    {runner.email_id}
                                  </a>
                                  <button
                                    onClick={() => copyToClipboard(runner.email_id, 'email', runner.email_id)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copy email address"
                                  >
                                    {copiedItems[`${runner.email_id}-email`] ? (
                                      <Check className="h-4 w-4" />
                                    ) : (
                                      <Copy className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              )}

                              {/* Location */}
                              {runner.location && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <svg className="h-4 w-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                  </svg>
                                  <span>{runner.location}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Coach Notes */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('coachNotes')}
                        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <Edit className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                          <span className="font-medium text-gray-900 text-sm sm:text-base">Coach Notes</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                          expandedSections.coachNotes ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections.coachNotes && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                          {/* Informational Message */}
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <svg className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">Internal Notes Only</p>
                                <p className="text-blue-700">Coach notes are for internal purposes only. The runner cannot see these notes.</p>
                              </div>
                            </div>
                          </div>

                          {/* Add Note Button */}
                          <div className="mb-4">
                            <button
                              onClick={() => {
                                if (typeof window !== 'undefined') {
                                  window.dispatchEvent(new CustomEvent('addNote', {
                                    detail: { runnerEmail: runner.email_id }
                                  }));
                                }
                              }}
                              className="flex items-center space-x-2 px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                            >
                              <Plus className="h-4 w-4" />
                              <span>Add Note</span>
                            </button>
                          </div>

                          {/* Notes Content */}
                          <RunnerCoachNotes runner={runner} />
                        </div>
                      )}
                    </div>

                    {/* Family Members */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('family')}
                        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <FamilyIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                          <span className="font-medium text-gray-900 text-sm sm:text-base">Family Members</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                          expandedSections.family ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections.family && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                          <RunnerFamilyMembers runner={runner} />
                        </div>
                      )}
                    </div>

                    {/* Club History */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('clubHistory')}
                        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
                          <span className="font-medium text-gray-900 text-sm sm:text-base">Club History</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                          expandedSections.clubHistory ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections.clubHistory && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                          <RunnerClubHistory runner={runner} />
                        </div>
                      )}
                    </div>

                    {/* Onboarding Survey */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('onboarding')}
                        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                          <span className="font-medium text-gray-900 text-sm sm:text-base">Onboarding Survey</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                          expandedSections.onboarding ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections.onboarding && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                          <RunnerOnboardingSurvey runner={runner} selectedSeason={selectedSeason} />
                        </div>
                      )}
                    </div>

                    {/* Race Timings */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection('raceTimings')}
                        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                          <span className="font-medium text-gray-900 text-sm sm:text-base">Race Timings</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
                          expandedSections.raceTimings ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {expandedSections.raceTimings && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                          {!selectedRunner ? (
                            <div className="text-gray-600 text-center py-4">
                              Select a runner to view their race timings
                            </div>
                          ) : raceTimingsLoading ? (
                            <div className="text-gray-600 text-center py-4">
                              Loading race timings...
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Message display */}
                              {raceTimingsMessage.text && (
                                <div className={`p-3 rounded-lg text-sm font-medium ${
                                  raceTimingsMessage.type === 'success' 
                                    ? 'bg-green-100 text-green-800 border border-green-200' 
                                    : 'bg-red-100 text-red-800 border border-red-200'
                                }`}>
                                  {raceTimingsMessage.text}
                                </div>
                              )}
                              
                              {/* Edit button */}
                              <div className="flex justify-end">
                                {editingRaceTimings ? (
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={handleSaveRaceTimings}
                                      className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={handleCancelRaceTimingsEdit}
                                      className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={handleEditRaceTimings}
                                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>

                              {/* Race Timings Form */}
                              <div className="space-y-4">
                                {/* Recent Activities Picker (edit mode only) */}
                                {editingRaceTimings && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Recent Activities — click to use as race timing</label>
                                    {recentActivitiesLoading ? (
                                      <div className="text-sm text-gray-500 italic">Loading recent activities...</div>
                                    ) : recentActivities.length === 0 ? (
                                      <div className="text-sm text-gray-400 italic">No recent run/walk activities found for this season.</div>
                                    ) : (
                                      <div className="flex flex-col gap-1">
                                        {recentActivities.map((act, idx, arr) => {
                                          // completed_time preferred; fall back to moving_time if null
                                          const rawTime = act.completed_time ?? act.moving_time;
                                          const secValue = parseFloat(rawTime);
                                          let displayTime = rawTime;
                                          let formTime = rawTime;
                                          if (!isNaN(secValue)) {
                                            const totalSec = Math.round(secValue);
                                            const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0');
                                            const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
                                            const ss = String(totalSec % 60).padStart(2, '0');
                                            displayTime = `${hh}:${mm}:${ss}`;
                                            formTime = displayTime;
                                          }
                                          const distanceMi = act.completed_distance
                                            ? `${parseFloat(act.completed_distance).toFixed(2)} mi`
                                            : '';
                                          return (
                                            <button
                                              key={idx}
                                              type="button"
                                              onClick={() => handleRaceTimingsFormChange('race_timings', formTime)}
                                              className="flex items-center justify-between w-full text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-sm"
                                            >
                                              <span className="text-gray-700">{act.workout_date}</span>
                                              {distanceMi && <span className="text-gray-500">{distanceMi}</span>}
                                              <span className="font-mono font-medium text-blue-700">{displayTime}</span>
                                            </button>
                                          );
                                        })}
                                        {recentActivitiesHasMore && (
                                          <button
                                            type="button"
                                            onClick={() => fetchRecentActivities(selectedRunner.email_id, recentActivitiesOffset)}
                                            disabled={recentActivitiesLoading}
                                            className="w-full text-center text-sm text-blue-600 hover:text-blue-800 py-1.5 border border-dashed border-blue-200 rounded-lg hover:border-blue-400 transition-colors disabled:opacity-50"
                                          >
                                            {recentActivitiesLoading ? 'Loading...' : 'Show 5 more activities'}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Race Timings, PR, and Race Distance in same row */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* Race Timings */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Race Timings</label>
                                    {editingRaceTimings ? (
                                      <div>
                                        <input
                                          type="text"
                                          value={raceTimingsFormData.race_timings}
                                          onChange={(e) => handleRaceTimingsFormChange('race_timings', e.target.value)}
                                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            raceTimingsValidation.race_timings ? 'border-red-300' : 'border-gray-300'
                                          }`}
                                          placeholder="HH:MM:SS (e.g., 01:30:45)"
                                          maxLength={8}
                                        />
                                        {raceTimingsValidation.race_timings && (
                                          <div className="text-xs text-red-600 mt-1">
                                            {raceTimingsValidation.race_timings}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg min-h-[40px]">
                                        {raceTimings?.race_timings || 'No race timings recorded'}
                                      </div>
                                    )}
                                  </div>

                                  {/* PR Checkbox */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">PR?</label>
                                    {editingRaceTimings ? (
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={raceTimingsFormData.race_pr}
                                          onChange={(e) => handleRaceTimingsFormChange('race_pr', e.target.checked)}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="text-sm text-gray-700">
                                          If its a PR, check this box
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg min-h-[40px] flex items-center">
                                        {raceTimings?.race_pr ? (
                                          <span className="text-green-600 font-medium">✓ Yes, this is a PR</span>
                                        ) : (
                                          <span className="text-gray-500">No, not a PR</span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Race Distance */}
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Race Distance</label>
                                    {editingRaceTimings ? (
                                      <select
                                        value={raceTimingsFormData.race_distance_completed}
                                        onChange={(e) => handleRaceTimingsFormChange('race_distance_completed', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      >
                                        <option value="">Select Race Distance</option>
                                        <option value="5K">5K</option>
                                        <option value="10K">10K</option>
                                        <option value="Half Marathon">Half Marathon</option>
                                        <option value="Marathon">Marathon</option>
                                        <option value="Other">Other</option>
                                      </select>
                                    ) : (
                                      <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg min-h-[40px] flex items-center">
                                        {raceTimings?.race_distance_completed || raceTimings?.race_distance || 'No race distance recorded'}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Race Comments - Full width */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Race Comments</label>
                                  {editingRaceTimings ? (
                                    <textarea
                                      value={raceTimingsFormData.race_comments}
                                      onChange={(e) => handleRaceTimingsFormChange('race_comments', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                      rows={3}
                                      placeholder="Enter race comments..."
                                    />
                                  ) : (
                                    <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg min-h-[60px]">
                                      {raceTimings?.race_comments || 'No race comments recorded'}
                                    </div>
                                  )}
                                </div>

                                {/* Helpful message for coaches */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                                  <div className="flex items-start space-x-2">
                                    <div className="flex-shrink-0">
                                      <svg className="h-4 w-4 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <div className="text-sm text-blue-800">
                                      <p className="font-medium mb-1">Race Timing Guidelines:</p>
                                      <p>For races with a timing chip, record the official chip time. You can verify it from the race results or ask the runner for a screenshot as proof.</p>
                                      <p className="mt-2">For races without a timing chip (e.g., logged in Strava or Final Surge), record the elapsed time, not the moving time.</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                    </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">
              No runners found in your cohort matching the current filters.
            </p>
          </div>
        )}
      </div>

      {/* Transfer Program Modal */}
      {showTransferModal && transferRunner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center space-x-3 p-6 border-b border-gray-200 flex-shrink-0">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <h2 className="text-xl font-bold text-gray-900">Transfer Program</h2>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-gray-700 mb-2">
                Transfer <span className="font-semibold">{transferRunner.runner_name}</span>
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Current Program Details:</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {/* Row 1 */}
                  <div>
                    <span className="text-xs text-gray-500">Activity:</span>
                    <span className="text-blue-600 font-medium ml-2">{transferRunnerActivity || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Segment:</span>
                    <span className="text-blue-600 font-medium ml-2">{transferRunnerSegment || 'N/A'}</span>
                  </div>

                  {/* Row 2 */}
                  <div>
                    <span className="text-xs text-gray-500">Race Distance:</span>
                    <span className="text-blue-600 font-medium ml-2">{transferRunner.race_distance || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Level:</span>
                    <span className="text-blue-600 font-medium ml-2">{transferRunnerLevel || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 mb-4">Select the new details (in order):</p>

              {/* Activity Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Activity
                </label>
                <select
                  value={selectedNewActivity || ''}
                  onChange={(e) => setSelectedNewActivity(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="" disabled>Select activity</option>
                  <option value="Run">Run</option>
                  <option value="Walk">Walk</option>
                </select>
              </div>

              {/* Segment Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Segment
                </label>
                <select
                  value={selectedNewSegment || ''}
                  onChange={(e) => setSelectedNewSegment(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="" disabled>Select segment</option>
                  <option value="Lite">Lite</option>
                  <option value="Pro">Pro</option>
                  <option value="Masters">Masters</option>
                </select>
              </div>

              {/* Race Distance / Program Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Race Distance
                </label>
                <div className="relative">
                  <button
                    onClick={() => setTransferDistanceMenuOpen(!transferDistanceMenuOpen)}
                    className={`w-full flex items-center justify-between p-3 text-left border rounded-lg transition-colors ${
                      selectedProgram ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50 text-gray-900'
                    }`}
                  >
                    <span className="font-medium">
                      {selectedProgram || 'Select Race Distance'}
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${transferDistanceMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {transferDistanceMenuOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      {['5K', '10K', 'Half Marathon', 'Full Marathon'].map((distance) => (
                        <button
                          key={distance}
                          onClick={() => {
                            handleProgramSelection(distance);
                            setTransferDistanceMenuOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                        >
                          {distance}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Level Selection - Only show for race distance programs (not Lite) */}
              {selectedProgram && selectedProgram !== 'Lite' && ['5K', '10K', 'Half Marathon', 'Full Marathon'].includes(selectedProgram) && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Level
                  </label>
                  <div className="relative transfer-level-dropdown">
                    <button
                      onClick={() => setTransferLevelMenuOpen(!transferLevelMenuOpen)}
                      className={`w-full flex items-center justify-between p-3 text-left border rounded-lg transition-colors ${
                        selectedNewLevel
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-gray-200 hover:bg-gray-50 text-gray-900'
                      }`}
                    >
                      <span className="font-medium">
                        {selectedNewLevel || 'Select Level'}
                      </span>
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${transferLevelMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {transferLevelMenuOpen && (
                      <div className="absolute bottom-full left-0 mb-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        {['Exp', 'Speed', 'Std'].map((level) => (
                          <button
                            key={level}
                            onClick={() => {
                              setSelectedNewLevel(level);
                              setTransferLevelMenuOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comment Section */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  value={transferComment}
                  onChange={(e) => setTransferComment(e.target.value)}
                  placeholder="Add any comments about this transfer request..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferRunner(null);
                  setTransferRunnerLevel(null);
                  setTransferRunnerActivity(null);
                  setTransferRunnerSegment(null);
                  setSelectedProgram(null);
                  setSelectedNewLevel(null);
                  setSelectedNewActivity(null);
                  setSelectedNewSegment(null);
                  setTransferComment('');
                  setTransferDistanceMenuOpen(false);
                  setTransferLevelMenuOpen(false);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleTransferSubmit}
                disabled={!selectedProgram}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  selectedProgram 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Transfer Modal */}
      {showConfirmationModal && transferRunner && selectedProgram && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Confirm Transfer</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to transfer <span className="font-semibold">{transferRunner.runner_name}</span>?
              </p>
              
              {/* Transfer Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">From:</span>
                  <div className="text-right">
                    <span className="font-medium text-gray-900">{transferRunner.race_distance}</span>
                    {transferRunnerLevel && (
                      <span className="text-gray-500 text-sm ml-2">({transferRunnerLevel})</span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">Activity:</span>
                  <div className="text-right">
                    <span className="font-medium text-gray-900">{transferRunnerActivity || 'N/A'}</span>
                    <span className="text-gray-500 text-sm ml-2">→</span>
                    <span className="font-medium text-blue-600 ml-2">{selectedNewActivity || 'N/A'}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">Segment:</span>
                  <div className="text-right">
                    <span className="font-medium text-gray-900">{transferRunnerSegment || 'N/A'}</span>
                    <span className="text-gray-500 text-sm ml-2">→</span>
                    <span className="font-medium text-blue-600 ml-2">{selectedNewSegment || 'N/A'}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">To:</span>
                  <div className="text-right">
                    <span className="font-medium text-blue-600">{selectedProgram}</span>
                    {selectedNewLevel && (
                      <span className="text-gray-500 text-sm ml-2">({selectedNewLevel})</span>
                    )}
                  </div>
                </div>
                {transferComment.trim() && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-gray-600 block mb-1">Comments:</span>
                    <span className="text-gray-900 text-sm">{transferComment}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowConfirmationModal(false);
                  setTransferRunner(null);
                  setTransferRunnerLevel(null);
                  setTransferRunnerActivity(null);
                  setTransferRunnerSegment(null);
                  setSelectedProgram(null);
                  setSelectedNewLevel(null);
                  setSelectedNewActivity(null);
                  setSelectedNewSegment(null);
                  setTransferComment('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedTransfer}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Confirm Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Defer Runner Modal */}
      {showDeferModal && deferRunner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center space-x-3 p-6 border-b border-gray-200">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-bold text-gray-900">Defer Runner</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Defer <span className="font-semibold">{deferRunner.runner_name}</span>
              </p>
              <p className="text-gray-600 mb-4">
                Current program: <span className="text-blue-600 font-medium">{deferRunner.race_distance}</span>
              </p>
              <p className="text-gray-700 mb-4">
                This will request to defer the runner from the current program to the next season. The request will be reviewed by the admin team.
              </p>

              {/* Comment Section */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  value={deferComment}
                  onChange={(e) => setDeferComment(e.target.value)}
                  placeholder="Add any comments about this defer request..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowDeferModal(false);
                  setDeferRunner(null);
                  setDeferComment('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeferSubmit}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Defer Modal */}
      {showDeferConfirmationModal && deferRunner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Confirm Defer Request</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to defer <span className="font-semibold">{deferRunner.runner_name}</span>?
              </p>
              
              {/* Defer Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Runner:</span>
                  <span className="font-medium text-gray-900">{deferRunner.runner_name}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-600">Current Program:</span>
                  <span className="font-medium text-orange-600">{deferRunner.race_distance}</span>
                </div>
                {deferComment.trim() && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <span className="text-gray-600 block mb-1">Comments:</span>
                    <span className="text-gray-900 text-sm">{deferComment}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowDeferConfirmationModal(false);
                  setDeferRunner(null);
                  setDeferComment('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedDefer}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                Confirm Defer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && notesRunner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    {notesRunner.runner_name ? notesRunner.runner_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Notes for {notesRunner.runner_name}</h2>
                  <p className="text-sm text-gray-500">{notesRunner.race_distance} • {notesRunner.location}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setNotesRunner(null);
                  setNotesContent('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-hidden">
              <div className="h-full flex flex-col">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coach Notes
                </label>
                <textarea
                  value={notesContent}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Enter your notes about this runner..."
                  className="flex-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={10}
                />
                {notesLoading && (
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Saving...
                  </div>
                )}
                {notesSaved && !notesLoading && (
                  <div className="mt-2 text-green-600 text-sm font-medium">Saved</div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowNotesModal(false);
                  setNotesRunner(null);
                  setNotesContent('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowYourRunner; 